import { useState, useRef } from "react";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "components/ui/badge/Badge";
import useDesktopChecker from "hooks/useDesktopChecker";
import "./table.scss";
import Modal from "components/modal/Modal";
import FileUploader from "components/file-uploader/FileUploader";
import {
  dashboardApi,
  NeedStatus,
  NeedType,
  PendingItem,
  useSetEdisclosureStatusToUnderReviewMutation,
  useUpdateDocumentIdsMutation,
  useUpdateTaskMessageMutation,
} from "pages/dashboard/home/dashboardService";
import { SuccessMessageModal } from "pages/dashboard/documents/Documents";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getCurrentUserApplicationId,
  getIsInitialDocUploaded,
  setIsInitialDocUploaded,
  getNhDealApplicationId,
  getCurrentUserId,
  setLogout,
} from "features/authSlice";
import { showDocuments } from "utilities/UserversionFeatures";
import GuideToolTip from "components/ui/guideToolTip/GuideToolTip";
import { tooltipIndexValues } from "pages/dashboard/home/Dashboard";
import {
  AdditionalDocumentationCondition,
  DASHBOARD_CONDITION_NEEDS_ATTENTION,
  DASHBOARD_NEEDS_STATUS_UNDER_REVIEW,
  DefaultConditionsCount,
  ESIGN_ACTION_TEXT,
  ESIGN_CLOSE_BUTTON_TEXT,
  ESIGN_CLOSE_DESCRIPTION,
  ESIGN_COMPLETED_TEXT,
  ESIGN_REDIRECTION_TOKEN,
  ESIGN_STATUS_COMPLETED,
  ESIGN_STATUS_NEW,
  ESIGN_STATUS_UNDER_REVIEW,
  ESIGN_TITLE,
  ESIGN_FOOTER_TEXT,
  PENDING_APPROVAL,
  UPLOAD_STATUS_ERROR,
  UPLOAD_STATUS_REUPLOAD,
  UPLOAD_STATUS_UPLOAD,
} from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import DocumentUploader from "components/document-uploader/DocumentUploader";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import InvalidDocumentPreview from "components/invalid-document-preview/InvalidDocumentPreview";
import { capitalizeFirstLetter } from "utilities/string";
import { ReactComponent as View } from "assets/images/View.svg";
import Drawer from "components/ui/drawer/Drawer";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import ElectronicSignature from "pages/e-sign/ElectronicSignature";

export interface TableProps {
  readonly Tablevalues: PendingItem[];
  readonly info?: string;
  readonly showTooltip?: boolean;
  readonly setTooltipIndex?: (index: number) => void;
  readonly isShowAllConditions: boolean;
  readonly checkEdisclosureStatus?: (currentNeed: PendingItem) => void;
}

export default function TableItems({ Tablevalues, info, showTooltip, setTooltipIndex, isShowAllConditions, checkEdisclosureStatus }: TableProps) {
  // Track which tasks have shown the toast
  const shownToastTasks = useRef<Set<string>>(new Set());
  const isDesktop = useDesktopChecker();
  const [currentConditionId, setCurrentConditionId] = useState("");
  const applicationId = useSelector(getCurrentUserApplicationId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const [UpdateDocumentIds] = useUpdateDocumentIdsMutation();
  const [UpdateTaskMessage] = useUpdateTaskMessageMutation();
  const [showDelayedTooltip, setShowDelayedTooltip] = useState(false);
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [showCommentSuccessPopup, setShowCommentSuccessPopup] = useState(false);
  const [showFileUploadPopup, setShowFileUploadPopup] = useState(false);
  const [showDefaultSuccessPopup, setShowDefaultSuccessPopup] = useState(false);
  const [isCommentSuccessPopup, setIsCommentSuccessPopup] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileId, setFileId] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [isESignDrawerOpen, setIsESignDrawerOpen] = useState(false);
  const [showEsignLoading, setShowEsignLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [setEdisclosureStatusToUnderReview] = useSetEdisclosureStatusToUnderReviewMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (showTooltip && !showDelayedTooltip) {
    setTimeout(() => {
      setShowDelayedTooltip(true);
    }, 3000);
  } else if (!showTooltip && showDelayedTooltip) {
    setShowDelayedTooltip(false);
  }
  const handleHide = (item: PendingItem, isViewComment: boolean) => {
    if (!showTooltip) {
      setCurrentConditionId(item.id);
      setToastMessage("");
      if (isViewComment) {
        setShowCommentPopup(!showCommentPopup);
      } else if (item?.needType === NeedType.ESign) {
        const status = (item?.status ?? "").toLowerCase();

        if (!item?.isEnabled) {
          // Currently, do nothing if e-sign is not enabled
        } else {
          localStorage.setItem(ESIGN_REDIRECTION_TOKEN, item.id);
          setShowEsignLoading(true);
          setIsESignDrawerOpen(true);
          // Show loading message for 3 seconds
          setTimeout(() => {
            setShowEsignLoading(false);
          }, 3000);
          if (status === ESIGN_STATUS_NEW || status === ESIGN_STATUS_UNDER_REVIEW) {
            void setEdisclosureStatusToUnderReview({
              nhDealApplicationId,
              prospectId: item.prospectId ?? item.nboContactId ?? "",
              packageId: item.packageId ?? "",
            });
          }
        }
      } else {
        setShowFileUploadPopup(!showFileUploadPopup);
      }
    }
  };

  const handleByAction = async () => {
    if (showFileUploadPopup) {
      await UpdateDocumentsIds(currentConditionId);
      setShowFileUploadPopup(!showFileUploadPopup);
    }
    if (isCommentSuccessPopup) {
      setShowCommentSuccessPopup(!showCommentSuccessPopup);
    } else {
      setShowDefaultSuccessPopup(!showDefaultSuccessPopup);
    }
  };
  const userId = useSelector(getCurrentUserId);
  const isInitialDocUploaded = useSelector(getIsInitialDocUploaded);
  const UpdateDocumentsIds = async (conditionId: string) => {
    try {
      if (nhDealApplicationId && userId) {
        await UpdateDocumentIds({
          nhDealApplicationId,
          borrowerId: userId,
          documentUpdateDto: {
            isInitialDocumentUploaded: isInitialDocUploaded,
          },
        });
      } 
      if (conditionId) {
        await UpdateTaskMessage({ nhDealApplicationId, conditionId, userId });
      }
      dispatch(setIsInitialDocUploaded(true));
    } catch (error) {
      console.error(`Error updating document id's`, error);
    }
  };

  const handleClick = () => setShowFileUploadPopup(!showFileUploadPopup);

  const handlePopupSwap = () => {
    setShowCommentPopup(false);
    setShowFileUploadPopup(true);
    setIsCommentSuccessPopup(true);
  };

  const handlePopupHide = () => {
    if (showCommentPopup) {
      setShowCommentPopup(!showCommentPopup);
    }
    if (showCommentSuccessPopup) {
      setShowCommentSuccessPopup(!showCommentSuccessPopup);
    }
    if (isCommentSuccessPopup) {
      setIsCommentSuccessPopup(!isCommentSuccessPopup);
    }
  };

  Tablevalues = Tablevalues.filter(
    (item) => isShowAllConditions || item.title === AdditionalDocumentationCondition || item.needType === NeedType.ESign
  );
  const tableItems = Tablevalues.length >= 1;
  const isNeedsListV3Enabled = featureFlag?.needsListV3Enabled ?? false;

  const isDocRequest = (item: PendingItem) => {
    if (!isNeedsListV3Enabled) return { isAdditionalDoc: false, isInvalidDoc: false };
    const hasPendingDocument = item.needStatus?.toLocaleLowerCase() === NeedStatus.PendingDocuments.toLocaleLowerCase();
    const isAdditionalDoc = !!item.note && hasPendingDocument;
    const isInvalidDoc =
      (hasPendingDocument && item.documents?.some((doc) => doc.userNotified && !!doc.invalidReason && !!doc.detailedExplanation)) ?? false;
    return { isAdditionalDoc, isInvalidDoc };
  };

  const handleViewComments = (item: PendingItem) => {
    const { isAdditionalDoc, isInvalidDoc } = isDocRequest(item);
    handleHide(item, isAdditionalDoc || isInvalidDoc);
  };

  const [invokeDocumentContent, { isLoading: isDocumentLoading, isFetching: isDocumentFetching }] =
    dashboardApi.endpoints.retrieveDocumentContent.useLazyQuery();

  const getBlob = async (documentId: string) => {
    try {
      const { data, isError, error } = await invokeDocumentContent(documentId);
      if (isError && typeof error === "object" && "status" in error && error.status === 401) {
        dispatch(setLogout());
        navigate("/", { replace: true, state: { path: location.pathname } });
        return "";
      } else if (data) {
        const blob = new Blob([Uint8Array.from(window.atob(data.fileContents), (c) => c.charCodeAt(0))], { type: data.contentType });
        const url = window.URL.createObjectURL(blob);
        setFileUrl(url);
        return url;
      } else {
        return "";
      }
    } catch (error) {
      console.error("Error in Retrieving document:", error);
      return "";
    }
  };

  const handlePreviewDocument = async (fileId: string, fileName: string) => {
    await getBlob(fileId);
    setFileId(fileId);
    setFileName(fileName);
    setShowCommentPopup(false);
    setPreviewDocument(true);
  };

  const handlePreviewDocumentClose = () => {
    setPreviewDocument(false);
    setShowCommentPopup(true);
  };

  if (isDocumentLoading || isDocumentFetching) {
    return (
      <div className="invalid-document-overlay">
        <Overlay />
      </div>
    );
  }

  const renderTaskAction = (needType: NeedType | undefined, status: string, isNeedEnable: boolean | undefined) => {
    if (needType === NeedType.ESign && (status.toLowerCase() === ESIGN_STATUS_NEW || status.toLowerCase() === ESIGN_STATUS_UNDER_REVIEW)) {
      if (isDesktop) {
        return (
          <span className={isNeedEnable ? "esign-action" : "esign-action-disabled"} aria-disabled={!isNeedEnable} role="button">
            <FontAwesomeIcon icon={solid("pen-line")} />
            &nbsp;
            <span>{ESIGN_ACTION_TEXT}</span>
          </span>
        );
      } else {
        return (
          <span className={isNeedEnable ? "esign-action" : "esign-action-disabled"} aria-disabled={!isNeedEnable} role="button">
            <FontAwesomeIcon icon={solid("pen-line")} size="lg" />
          </span>
        );
      }
    }

    if (needType === NeedType.ESign && status.toLowerCase() === ESIGN_STATUS_COMPLETED) {
      return isDesktop ? (
        <span className={isNeedEnable ? "esign-completed-action" : "esign-completed-action-disabled"} aria-disabled={!isNeedEnable} role="button">
          <View />
          &nbsp;
          <span>{ESIGN_COMPLETED_TEXT}</span>
        </span>
      ) : (
        <span className={isNeedEnable ? "esign-completed-action" : "esign-completed-action-disabled"} aria-disabled={!isNeedEnable} role="button">
          <View />
        </span>
      );
    }

    const isError = status === UPLOAD_STATUS_ERROR;

    return isDesktop ? (
      <span>
        <FontAwesomeIcon icon={solid("cloud-arrow-up")} />
        &nbsp;
        <span>{isError ? UPLOAD_STATUS_REUPLOAD : UPLOAD_STATUS_UPLOAD}</span>
      </span>
    ) : (
      <span>
        <FontAwesomeIcon icon={solid("cloud-arrow-up")} size="lg" />
      </span>
    );
  };

  const handleESignDrawerClose = () => {
    setIsESignDrawerOpen(false);
    setShowEsignLoading(false);
    const currentTask = Tablevalues.find((item) => item.id === currentConditionId);
    // Clear the redirection token
    localStorage.removeItem(ESIGN_REDIRECTION_TOKEN);
    if (
      currentTask &&
      currentTask.needType === NeedType.ESign &&
      (currentTask.status.toLowerCase() === ESIGN_STATUS_NEW || currentTask.status.toLowerCase() === ESIGN_STATUS_UNDER_REVIEW)
    ) {
      checkEdisclosureStatus?.(currentTask);
    }
  };

  return (
    <>
      <div className="table">
        <div className={!isShowAllConditions || Tablevalues.length == DefaultConditionsCount ? "table-content" : "table-items"}>
          {Tablevalues.map((item, index) => {
            return (
              <>
                <div
                  className={`table-items__main ${isDesktop && tableItems ? "item" : ""}  ${
                    showDelayedTooltip && index === 0 ? "table-items__highlight" : ""
                  }`}
                  key={item.id}
                >
                  <div className="table-items__container">
                    <div className={showDocuments() ? "table-items__titles" : "table-items__titles--max_width"}>{item.title}</div>
                    {showDocuments() && (
                      <NeedLineItemStatus item={item} showDelayedTooltip={showDelayedTooltip} onViewComments={() => handleViewComments(item)} />
                    )}
                  </div>
                  {showDocuments() && (
                    <div
                      className={`table-items__action ${showDelayedTooltip ? "table-items__action__tooltip--show" : ""}`}
                      onClick={() => {
                        const { isAdditionalDoc, isInvalidDoc } = isDocRequest(item);
                        handleHide(item, isAdditionalDoc || isInvalidDoc);
                      }}
                    >
                      {renderTaskAction(item.needType, item.status, item.isEnabled)}
                    </div>
                  )}
                  {showFileUploadPopup && currentConditionId == item.id && (
                    <Modal
                      customBody={
                        <FileUploader
                          handleByAction={() => handleByAction()}
                          onClick={handleClick}
                          operationType="UploadDocumentsWithSpecificCondition"
                          conditionId={item.id}
                          navigate={navigate}
                        />
                      }
                      defaultHeader={false}
                      defaultBody={false}
                    />
                  )}
                  {currentConditionId == item.id && (showCommentPopup || showCommentSuccessPopup) && (
                    <DocumentUploader
                      pendingItems={item}
                      handleByAction={handlePopupSwap}
                      onClick={handlePopupHide}
                      isUploadSuccess={showCommentSuccessPopup}
                      hasInvalidDocuments={isDocRequest(item).isInvalidDoc}
                      handlePreview={handlePreviewDocument}
                    />
                  )}
                  {showDefaultSuccessPopup && <SuccessMessageModal handleByAction={() => handleByAction()} />}
                  {currentConditionId == item.id && previewDocument && (
                    <InvalidDocumentPreview fileId={fileId} fileName={fileName} onClose={handlePreviewDocumentClose} fileUrl={fileUrl} />
                  )}
                  {currentConditionId == item.id && isESignDrawerOpen && (
                    <Drawer
                      isOpen={isESignDrawerOpen}
                      onClose={() => handleESignDrawerClose()}
                      title={ESIGN_TITLE}
                      closeDescription={ESIGN_CLOSE_DESCRIPTION}
                      closeText={ESIGN_CLOSE_BUTTON_TEXT}
                      footerText={ESIGN_FOOTER_TEXT}
                    >
                      <>
                        {showEsignLoading && (
                          <div className="esign-loading-overlay">
                            <Overlay text="One moment while we get your documents..." />
                          </div>
                        )}
                        <ElectronicSignature 
                          loanAccessToken={item?.lat} 
                          packageId={item?.packageId} 
                        />
                      </>
                    </Drawer>
                  )}
                </div>
                {index === 0 && showDelayedTooltip && (
                  <div className="tooltip-popup">
                    <GuideToolTip
                      contentHeading="Pro Tip"
                      tooltipNo="1 of 3"
                      content="Upload requested documents for each task to
                      complete them."
                      cancelClick={() => {
                        setTooltipIndex?.(tooltipIndexValues.completed);
                      }}
                      buttonOnClick={() => {
                        setTooltipIndex?.(tooltipIndexValues.milestones);
                      }}
                      buttonName="Next"
                    />
                  </div>
                )}
                {toastMessage && shownToastTasks.current.has(item.id) && (
                  <ToastMessage header="Completed" message={toastMessage} state="success" position="top-right" />
                )}
              </>
            );
          })}
        </div>
      </div>
      {showDocuments() && showDelayedTooltip && <div className="transparent-overlay"></div>}
    </>
  );
}

interface NeedLineItemStatusProps {
  item: PendingItem;
  showDelayedTooltip: boolean;
  onViewComments: () => void;
}
function NeedLineItemStatus({ item, showDelayedTooltip, onViewComments }: Readonly<NeedLineItemStatusProps>) {
  let badgeColor = item.status.toLowerCase();
  let badgeTitle: string = capitalizeFirstLetter(item.status);
  let containerClassName = "table-items__status";
  if (showDelayedTooltip) {
    containerClassName += " table-items__status__tooltip--show";
  }

  const isViewCommentsVisible = () => item.status === DASHBOARD_CONDITION_NEEDS_ATTENTION;

  switch (item.status) {
    case DASHBOARD_NEEDS_STATUS_UNDER_REVIEW:
      badgeTitle = "Under Review";
      break;
    case DASHBOARD_CONDITION_NEEDS_ATTENTION:
      badgeColor = "need-attention";
      badgeTitle = "Needs Attention";
      break;

    case PENDING_APPROVAL:
      badgeColor = "pending-approval";
      if (item.note && item.needStatus === NeedStatus.PendingDocuments) {
        badgeColor = "uploaded";
        badgeTitle = "Uploaded";
      }
      break;
  }

  return (
    <div className={containerClassName}>
      {isViewCommentsVisible() && (
        <button className="table-items__viewcomments" onClick={onViewComments}>
          View Comments
        </button>
      )}
      <Badge color={badgeColor} title={badgeTitle} toUpperCase />
    </div>
  );
}
